import axios from 'axios';
import { currentLanguage } from '../i18n';

// Configure API base URL based on environment
const getBaseURL = () => {
  // In development, use the proxy setup (package.json has proxy to localhost:8888 for MAMP)
  if (process.env.NODE_ENV === 'development') {
    return ''; // Empty string uses the proxy configured in package.json (MAMP on port 8888)
  }
  
  // In production, try the standard backend path first
  return '/backend';
};

// Create axios instance with base configuration
const api = axios.create({
  baseURL: getBaseURL(),
  headers: {
    'Content-Type': 'application/json',
    // This bundle stores item values by field key and places as location_id,
    // and knows kinds (locked preset fields, option codes, vocab fields); the
    // server refuses writes from older bundles (409 client_outdated), so they
    // cannot write by name, put a place in field_values or break a preset.
    'X-Inventorium-Fields': 'keys, locations, kinds',
  },
});

/**
 * Address of a media variant ('thumb' 160 px, 'medium' 640 px, 'original'),
 * served by the API on the same origin with the session cookie.
 */
export const mediaUrl = (id, variant = 'thumb') => `${getBaseURL()}/media/${id}/${variant}`;

/** Uploads one image to a collection; onProgress gets 0–100. Resolves to {id, width, height, url}. */
export const uploadMedia = (inventoryId, file, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/inventories/${inventoryId}/media`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (event) => {
      if (onProgress && event.total) onProgress(Math.round((event.loaded / event.total) * 100));
    },
  }).then(response => response.data);
};

let csrfToken = null;
let onUnauthorized = () => {};
let onServiceNotice = () => {};

export const setCsrfToken = (token) => { csrfToken = token; };
export const setUnauthorizedHandler = (handler) => { onUnauthorized = handler; };
// Called with 'client_outdated' (reload needed) or 'maintenance' (release under way).
export const setServiceNoticeHandler = (handler) => { onServiceNotice = handler; };

// Every state-changing request carries the CSRF token the server last handed out;
// every request carries the language on screen as Accept-Language, so what the
// server writes for it (a reset e-mail, a new type's field names) matches what
// the person reads, not just the browser's setting.
api.interceptors.request.use((config) => {
  if (csrfToken && config.method !== 'get') {
    config.headers['X-CSRF-Token'] = csrfToken;
  }
  config.headers['Accept-Language'] = currentLanguage();
  return config;
});

// Any response may carry a fresh token (login and logout rotate it); a 401 outside
// /auth/ means the session is gone, so the gate goes back to the login screen.
api.interceptors.response.use(
  (response) => {
    if (response.data && response.data.csrf_token) {
      csrfToken = response.data.csrf_token;
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401 && !error.config?.url?.startsWith('/auth/')) {
      onUnauthorized();
    }
    const code = error.response?.data?.code;
    if (code === 'client_outdated' || code === 'maintenance') {
      onServiceNotice(code);
    }
    return Promise.reject(error);
  }
);

export default api;